import React, { useContext, useMemo, useRef, useState } from 'react'
import { format as fmtDate } from 'date-fns'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { Controller } from 'react-hook-form'

import { context, setAnytimeToday, setTimeSlot, useSelectors } from '../../store'
import { fields, slots } from '../../store/constants'
import { AnalyticsService } from '../../services/analytics'
import { useLivePrice } from '../../lib/use-live-price'
import { sameDayArrivalNote, sameDayOpen } from '../../lib/same-day'
import { dateCorrectionProps, parseDateInput, resolveBookingDate, startOfDayPlus } from '../../lib/date-input'
import { chipEventProps, dayChips, isChipDay, shortDay } from '../../lib/day-chips'
import { radioGroupKeyDown } from '../../lib/radio-keys'
import { SAME_DAY_LABEL, unavailableReason } from '../../lib/same-day-offer'
import { SameDayOffer } from '../same-day-offer'
import { dayState, isOff, stateNote } from '../../lib/availability'
import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// A mouse or trackpad: a click on a date box does not open its calendar in
// desktop Chrome, so it is asked to. A finger on a phone opens it natively.
const finePointer = () => {
  try { return !!window.matchMedia && window.matchMedia('(pointer: fine)').matches } catch (e) { return false }
}

const openPicker = input => {
  if (!input) return
  try {
    if (typeof input.showPicker === 'function') input.showPicker()
    else input.focus()
  } catch (e) { input.focus() }
}

// The day, as chips (Book & pay, since 9 Oct 2026): Today +£25 first while
// same-day is open, then twelve days, none chosen on load, then "More dates"
// for anything further, which opens the browser's own calendar. It replaces
// the native date box there, which iOS Safari draws empty, desktop Chrome
// opened only from its small icon, and whose phone wheel could land on today
// after the cutoff. lib/day-chips.js has the days; the cutoff is London's.
//
// `source` names where it is shown in the events: 'chip' on the form,
// 'pay_panel' when the pay buttons ask for the day. Both write the same
// field; the panel's copy hides "More dates" so it stays one row of taps.
// `availability`: the days for the car's district (lib/availability.js, Form
// v3): an unavailable day is greyed and cannot be tapped, a limited one says
// why under its date.
export const DayChips = ({ form, required = true, source = 'chip', moreDates = true, onPicked, availability }) => {
  const classes = useStyles()
  const { dispatch } = useContext(context)
  const { sameDay, prices } = useSelectors()
  const priceFor = useLivePrice()
  const sameDayPrice = priceFor('Extra for same day')
  const moreRef = useRef(null)
  const [moved, setMoved] = useState(null)
  // Why the day was moved, for the WhatsApp offer under it: 'too_late'
  // (today, after the cutoff) or 'moved' (lib/same-day-offer.js).
  const [movedReason, setMovedReason] = useState('moved')
  const getPostcode = () => form.getValues(fields.postcode)

  const todayOpen = sameDayOpen()
  const chips = useMemo(() => dayChips(new Date(), { todayOpen }), [todayOpen])

  const pick = (requested, fieldOnChange, how) => {
    const now = new Date()
    const { date, sameDay: pickedToday, corrected } = resolveBookingDate(requested, now, sameDayOpen(now))
    setMoved(corrected ? date : null)
    if (corrected) {
      AnalyticsService.trackBookingDateCorrected(dateCorrectionProps(requested, false, now))
      const reason = unavailableReason({ requestedToday: startOfDayPlus(requested, 0).getTime() === startOfDayPlus(now, 0).getTime() })
      setMovedReason(reason)
      AnalyticsService.trackSameDayUnavailable(reason)
    }
    AnalyticsService.trackBookingDateChipSelected(chipEventProps(date, now, how))
    if (pickedToday) {
      const wasSameDay = !!sameDay
      dispatch(setAnytimeToday(true))
      dispatch(setTimeSlot(slots.today))
      form.setValue(fields.timeSlot, slots.today, { shouldValidate: true })
      if (!wasSameDay) {
        const id = prices?.ids?.find(pid => prices.byId[pid]?.name === 'Extra for same day')
        const pounds = id ? prices.byId[id]?.price?.value : null
        AnalyticsService.trackSameDaySurchargeApplied(typeof pounds === 'number' ? pounds : null)
      }
    } else if (sameDay) {
      // Leaving today: the same-day slot goes, TimeSlots puts Anytime back.
      dispatch(setAnytimeToday(false))
      dispatch(setTimeSlot(null))
      form.setValue(fields.timeSlot, null, { shouldValidate: false })
    }
    fieldOnChange(date)
    onPicked && onPicked(date)
  }

  return (
    <Controller
      name={fields.date}
      control={form.control}
      rules={{ required: required && 'Pick a day' }}
      render={({ field: { ref, value, onChange }, fieldState: { error } }) => {
        const chosen = value instanceof Date && !isNaN(value) ? startOfDayPlus(value, 0).getTime() : null
        const moreChosen = chosen !== null && !isChipDay(value, chips)
        return (
          <div className={classes.root} data-testid={source === 'chip' ? 'day-chips' : 'day-chips-panel'}>
            {source === 'chip' && <span className={classes.title}>Date</span>}
            <div className={clsx(classes.grid, source !== 'chip' && classes.gridCompact)} role="radiogroup" aria-label="Day" aria-invalid={!!error} onKeyDown={radioGroupKeyDown}>
              {chips.map(chip => {
                const state = dayState(availability, chip.date)
                const note = stateNote(state)
                return (
                <button
                  key={chip.date.getTime()}
                  type="button"
                  role="radio"
                  disabled={isOff(state)}
                  aria-checked={chosen === chip.date.getTime()}
                  aria-label={`${chip.isToday ? `Today, ${SAME_DAY_LABEL}${sameDayPrice ? ` +${sameDayPrice}` : ''}` : fmtDate(chip.date, 'EEEE d MMMM')}${note ? `, ${note}` : ''}`}
                  className={clsx(classes.chip, chosen === chip.date.getTime() && classes.chipOn, error && classes.chipInvalid, chip.isToday && classes.chipToday)}
                  onClick={() => pick(chip.date, onChange, source)}>
                  <span className={classes.weekday}>{chip.weekday}</span>
                  {/* "Same-day priority +£25" (17 Oct 2026), on a chip a
                      whole row wide (26 Oct 2026). */}
                  {chip.isToday && <span className={classes.priority}>{SAME_DAY_LABEL}</span>}
                  {/* A new month's first chip: "1" with a smaller "Nov",
                      so it stays inside a phone-width chip (25 Oct 2026). */}
                  <span className={classes.day}>
                    {chip.isToday ? (sameDayPrice ? `+${sameDayPrice}` : 'extra') : chip.day}
                    {chip.month && <span className={classes.month}> {chip.month}</span>}
                  </span>
                  {note && <span className={classes.weekday}>{note}</span>}
                </button>
                )
              })}
              {moreDates && (
                // The native date box lies over the chip, invisible, so a tap
                // on "More dates" is a tap on the date box itself, which every
                // phone opens with its own calendar. Until 25 Oct 2026 the box
                // was a 1px one off to the side and the chip called
                // showPicker() on it: Chrome on iPhone (WebKit) has
                // showPicker but opened nothing, a dead tap (funnel review
                // 25 Oct 2026, replay 01a16894: four taps, then gone). A mouse
                // still needs showPicker(): desktop Chrome opens the calendar
                // only from its own small icon. The chip under it keeps the
                // look and the keyboard (Tab, Enter).
                <div className={clsx(classes.moreWrap, classes.chipMore)}>
                  <button
                    type="button"
                    role="radio"
                    data-radio-skip
                    aria-checked={moreChosen}
                    className={clsx(classes.chip, classes.moreChip, moreChosen && classes.chipOn, error && classes.chipInvalid)}
                    onClick={() => {
                      AnalyticsService.trackBookingMoreDatesOpened()
                      openPicker(moreRef.current)
                    }}>
                    {moreChosen ? (
                      <>
                        <span className={classes.weekday}>{shortDay(value).split(' ')[0]}</span>
                        <span className={classes.day}>
                          {shortDay(value).split(' ')[1]}
                          {shortDay(value).split(' ')[2] && <span className={classes.month}> {shortDay(value).split(' ')[2]}</span>}
                        </span>
                      </>
                    ) : (
                      <span className={classes.more}>More dates</span>
                    )}
                  </button>
                  <input
                    ref={el => { moreRef.current = el; ref(el) }}
                    type="date"
                    name="date"
                    tabIndex={-1}
                    aria-hidden="true"
                    data-testid="more-dates-input"
                    className={classes.moreInput}
                    min={fmtDate(startOfDayPlus(new Date(), todayOpen ? 0 : 1), 'yyyy-MM-dd')}
                    value={chosen !== null ? fmtDate(value, 'yyyy-MM-dd') : ''}
                    onClick={ev => {
                      AnalyticsService.trackBookingMoreDatesOpened()
                      if (finePointer()) openPicker(ev.currentTarget)
                    }}
                    onChange={ev => { if (ev.target.value) pick(parseDateInput(ev.target.value), onChange, 'more_dates') }}
                  />
                </div>
              )}
            </div>
            {error && !value && <p className={classes.error} role="alert">Pick a day</p>}
            {moved && value && !sameDay && (
              <p className={classes.hint} role="status">That day isn&rsquo;t available, so we&rsquo;ve moved it to {shortDay(moved)}.</p>
            )}
            {/* Need it today anyway: WhatsApp (lib/same-day-offer.js). */}
            {moved && value && !sameDay && <SameDayOffer reason={movedReason} getPostcode={getPostcode} />}
            {sameDay && source === 'chip' && (
              <p className={classes.sameDayNote}>
                {sameDayPrice ? `${SAME_DAY_LABEL} +${sameDayPrice}. ${sameDayArrivalNote()}` : `${SAME_DAY_LABEL} is an extra charge. ${sameDayArrivalNote()}`}
              </p>
            )}
            {sameDay && source === 'chip' && <SameDayOffer reason="today_selected" getPostcode={getPostcode} />}
          </div>
        )
      }}
    />
  )
}
